/**
 * Состояние фильтров, сортировки и представлений (§10.1–10.5, §4.5, §5.4).
 *
 * Единственный источник правды — query-строка (§10.5): ссылку можно скинуть в
 * чат, и коллега увидит буквально твой экран. Хук читает её, отдаёт готовый
 * набор строк и пишет обратно только свои параметры: `cols`, `open`, `sel`,
 * `density` принадлежат таблице и остаются нетронутыми.
 *
 * ── Снимок строк (§5.4, П2) ────────────────────────────────────────────────
 * `visibleBrands` пересчитывается ровно по тем поводам, которые перечислены в
 * спеке: смена фильтра, сортировки, канала, представления (всё это — смена
 * query-строки) и обновление опубликованного среза (новый массив `brands` из
 * стора: «Обновить», публикация пакета, создание бренда). Правки в черновике
 * в пересчёт НЕ входят: они приходят через ref, поэтому строка, которую вы
 * только что заполнили, не исчезает под курсором. Вместо исчезновения
 * возвращается `staleBrandIds` — строки, которые уйдут из фильтра после
 * «Обновить» (иконка ⤓ в gutter и строка в пилюлях).
 */
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react'
import { useSearchParams } from 'react-router-dom'
import type {
  Brand, ChannelCode, FieldDefinition, SavedView, SortRule, ViewConfig,
} from '@shared/types.ts'
import { api } from '../../../lib/api.js'
import { brandHasDraft } from '../../../lib/draft-overlay.js'
import { useApp } from '../../../store/app-store.js'
import {
  applyFilters, countActiveGroups, countChips, matchesFilters, matchesStatus,
  sortBrands, SYSTEM_AUTHOR_ID, UNSET,
  type BooleanFilterKey, type BrandFilters, type ChipCounts, type FieldFilter,
  type FilterContext, type ValueGroupKey, type WhiteGloveField,
} from './apply.js'
import { filterTexts } from './texts.filters.js'

// ───────────────────────────── параметры URL (§10.5) ─────────────────────────

/** «Не задано» в списке значений: `tier=A+,none`. Исключение — префикс `-`. */
const NONE = 'none'

const VALUE_GROUPS: readonly { param: string; key: ValueGroupKey }[] = [
  { param: 'tier', key: 'tiers' },
  { param: 'vis', key: 'visibility' },
  { param: 'seo', key: 'seoPriority' },
  { param: 'ctx', key: 'contextPriority' },
  { param: 'stock', key: 'stockDisplay' },
  { param: 'oos', key: 'oos' },
]

/**
 * Флаги-тумблеры. `on` — значение признака, при котором чип «включён»;
 * обратное значение и есть исключение (Alt+клик), поэтому отдельного
 * параметра для исключения не нужно: `nourl=1` — «Без URL», `nourl=0` — «− Без URL».
 */
const FLAGS: readonly { param: string; key: BooleanFilterKey; on: boolean }[] = [
  { param: 'nourl', key: 'hasUrl', on: false },
  { param: 'unclear', key: 'categoriesUnclear', on: true },
  { param: 'cmt', key: 'hasComments', on: true },
  { param: 'diff', key: 'channelDiff', on: true },
  { param: 'one', key: 'onlyOneChannel', on: true },
  { param: 'mine', key: 'onlyMine', on: true },
  { param: 'lens', key: 'lensVisit', on: true },
  { param: 'todo', key: 'todo', on: true },
  { param: 'miss', key: 'myMissing', on: true },
]

/** Все параметры, которыми владеет этот модуль. Остальные не трогаем. */
const OWNED = [
  'q', 'cat', 'catmode', 'pres', 'wg', 'chg', 'by', 'st', 'sort', 'view',
  ...VALUE_GROUPS.map((group) => group.param),
  ...FLAGS.map((flag) => flag.param),
]

const FIELD_PREFIX = 'f.'

function splitList(raw: string | null): { include: string[]; exclude: string[] } {
  const include: string[] = []
  const exclude: string[] = []
  for (const token of (raw ?? '').split(',')) {
    const value = token.trim()
    if (value === '') continue
    if (value.startsWith('-')) {
      const body = value.slice(1)
      exclude.push(body === NONE ? UNSET : body)
    } else {
      include.push(value === NONE ? UNSET : value)
    }
  }
  return { include, exclude }
}

function joinList(
  include: readonly (string | null)[] | undefined,
  exclude: readonly (string | null)[] | undefined,
): string {
  const tokens = [
    ...(include ?? []).map((value) => (value === null || value === UNSET ? NONE : value)),
    ...(exclude ?? []).map((value) => `-${value === null || value === UNSET ? NONE : value}`),
  ]
  return tokens.join(',')
}

function splitNumbers(raw: string | null): { include: number[]; exclude: number[] } {
  const { include, exclude } = splitList(raw)
  const toNumbers = (list: string[]) =>
    list.map((value) => Number(value)).filter((value) => Number.isFinite(value))
  return { include: toNumbers(include), exclude: toNumbers(exclude) }
}

function joinNumbers(include: readonly number[] | undefined, exclude: readonly number[] | undefined): string {
  return [...(include ?? []).map(String), ...(exclude ?? []).map((id) => `-${id}`)].join(',')
}

// ── пользовательские поля в URL: `f.<код>=…` ────────────────────────────────

function encodeFieldFilter(filter: FieldFilter): string {
  switch (filter.kind) {
    case 'select':
      return filter.values.map((value) => (value === UNSET ? NONE : value)).join(',')
    case 'user':
      return filter.values.join(',')
    case 'number':
      return `${filter.min ?? ''}..${filter.max ?? ''}`
    case 'date':
      return `${filter.from ?? ''}..${filter.to ?? ''}`
    case 'bool':
      return filter.value === null ? NONE : filter.value ? '1' : '0'
    case 'text':
      return filter.filled ? 'filled' : 'empty'
  }
}

function decodeFieldFilter(def: FieldDefinition, raw: string): FieldFilter | null {
  if (raw === '') return null
  switch (def.type) {
    case 'select':
    case 'multiselect':
      return { kind: 'select', values: splitList(raw).include }
    case 'user':
      return { kind: 'user', values: splitNumbers(raw).include }
    case 'number': {
      const [min = '', max = ''] = raw.split('..')
      const parsed = {
        kind: 'number' as const,
        min: min === '' ? undefined : Number(min),
        max: max === '' ? undefined : Number(max),
      }
      if (parsed.min !== undefined && !Number.isFinite(parsed.min)) return null
      if (parsed.max !== undefined && !Number.isFinite(parsed.max)) return null
      return parsed.min === undefined && parsed.max === undefined ? null : parsed
    }
    case 'date': {
      const [from = '', to = ''] = raw.split('..')
      if (from === '' && to === '') return null
      return { kind: 'date', from: from || undefined, to: to || undefined }
    }
    case 'bool':
      return { kind: 'bool', value: raw === NONE ? null : raw === '1' }
    default:
      return { kind: 'text', filled: raw !== 'empty' }
  }
}

/** Разбор query-строки в фильтр. Неизвестные значения игнорируются молча (§10.5). */
export function readFilters(search: URLSearchParams, fields: readonly FieldDefinition[]): BrandFilters {
  const filters: BrandFilters = {}
  const exclude: NonNullable<BrandFilters['exclude']> = {}

  const query = search.get('q')?.trim()
  if (query) filters.query = query

  for (const group of VALUE_GROUPS) {
    const { include, exclude: out } = splitList(search.get(group.param))
    if (include.length > 0) filters[group.key] = include
    if (out.length > 0) exclude[group.key] = out
  }

  for (const flag of FLAGS) {
    const raw = search.get(flag.param)
    if (raw === '1') filters[flag.key] = flag.on
    else if (raw === '0') filters[flag.key] = !flag.on
  }

  const cats = splitNumbers(search.get('cat'))
  if (cats.include.length > 0) filters.categoryIds = cats.include
  if (cats.exclude.length > 0) exclude.categoryIds = cats.exclude
  if (search.get('catmode') === 'all') filters.categoriesMode = 'all'

  const present = search.get('pres')
  if (present === 'retail' || present === 'b2b' || present === 'both' || present === 'only_one') {
    filters.presentIn = present
  }

  const wg = splitList(search.get('wg')).include
    .filter((value): value is WhiteGloveField =>
      value === 'delivery' || value === 'installation' || value === 'warranty')
  if (wg.length > 0) filters.whiteGlove = wg

  const changed = Number(search.get('chg'))
  if (Number.isFinite(changed) && changed > 0) filters.changedWithinDays = changed

  const authors = splitNumbers(search.get('by'))
  if (authors.include.length > 0) filters.changedBy = authors.include
  if (authors.exclude.length > 0) exclude.changedBy = authors.exclude

  const status = search.get('st')
  if (status === 'archived' || status === 'all') filters.status = status

  const fieldFilters: Record<string, unknown> = {}
  for (const [param, raw] of search.entries()) {
    if (!param.startsWith(FIELD_PREFIX)) continue
    const code = param.slice(FIELD_PREFIX.length)
    const def = fields.find((item) => item.code === code)
    if (!def) continue
    const parsed = decodeFieldFilter(def, raw)
    if (parsed) fieldFilters[code] = parsed
  }
  if (Object.keys(fieldFilters).length > 0) filters.fields = fieldFilters

  if (Object.keys(exclude).length > 0) filters.exclude = exclude
  return filters
}

/** Запись фильтра в query-строку: сначала стираем свои параметры, потом пишем. */
export function writeFilters(search: URLSearchParams, filters: BrandFilters): void {
  for (const param of OWNED) if (param !== 'sort' && param !== 'view') search.delete(param)
  for (const param of [...search.keys()]) {
    if (param.startsWith(FIELD_PREFIX)) search.delete(param)
  }

  if (filters.query) search.set('q', filters.query)

  const exclude = filters.exclude
  for (const group of VALUE_GROUPS) {
    const value = joinList(filters[group.key], exclude?.[group.key])
    if (value) search.set(group.param, value)
  }

  for (const flag of FLAGS) {
    const value = filters[flag.key]
    if (value === undefined) continue
    search.set(flag.param, value === flag.on ? '1' : '0')
  }

  const cats = joinNumbers(filters.categoryIds, exclude?.categoryIds)
  if (cats) search.set('cat', cats)
  if (filters.categoriesMode === 'all') search.set('catmode', 'all')
  if (filters.presentIn) search.set('pres', filters.presentIn)
  if (filters.whiteGlove && filters.whiteGlove.length > 0) search.set('wg', filters.whiteGlove.join(','))
  if (filters.changedWithinDays) search.set('chg', String(filters.changedWithinDays))

  const authors = joinNumbers(filters.changedBy, exclude?.changedBy)
  if (authors) search.set('by', authors)
  if (filters.status && filters.status !== 'active') search.set('st', filters.status)

  for (const [code, raw] of Object.entries(filters.fields ?? {})) {
    const filter = raw as FieldFilter | null
    if (!filter || typeof filter !== 'object' || !('kind' in filter)) continue
    const encoded = encodeFieldFilter(filter)
    if (encoded) search.set(`${FIELD_PREFIX}${code}`, encoded)
  }
}

// ───────────────────────────── сортировка (§10.3) ────────────────────────────

export type SortPresetCode = 'alphabet' | 'priority' | 'recent' | 'unfilled'

export const SORT_PRESETS: Record<SortPresetCode, SortRule[]> = {
  alphabet: [{ column: 'name', direction: 'asc' }],
  priority: [{ column: 'tier', direction: 'asc' }, { column: 'name', direction: 'asc' }],
  recent: [{ column: 'changed', direction: 'desc' }],
  unfilled: [{ column: 'missing', direction: 'desc' }, { column: 'name', direction: 'asc' }],
}

export const SORT_PRESET_LABELS: Record<SortPresetCode, string> = {
  alphabet: filterTexts.sort.alphabet,
  priority: filterTexts.sort.priority,
  recent: filterTexts.sort.recent,
  unfilled: filterTexts.sort.unfilled,
}

function readSort(search: URLSearchParams, fallback: SortRule[]): SortRule[] {
  const raw = search.get('sort')
  if (!raw) return fallback
  const rules: SortRule[] = []
  for (const token of raw.split(',')) {
    const value = token.trim()
    if (value === '') continue
    const desc = value.startsWith('-')
    const column = desc ? value.slice(1) : value
    if (column === '') continue
    rules.push({ column, direction: desc ? 'desc' : 'asc' })
  }
  return rules.length > 0 ? rules : fallback
}

function writeSort(rule: SortRule): string {
  return `${rule.direction === 'desc' ? '-' : ''}${rule.column}`
}

function sameSort(a: readonly SortRule[], b: readonly SortRule[]): boolean {
  return a.length === b.length
    && a.every((rule, index) => rule.column === b[index]?.column && rule.direction === b[index]?.direction)
}

// ───────────────────────────── представления (§10.4) ─────────────────────────

/**
 * Фильтры представления в двух видах: рабочем и сохраняемом.
 *
 * `POST /api/views` проверяет конфиг схемой `.strict()` по `shared/types.ts`:
 * расширения §10.2 (исключения, «Доделки», White Glove, расхождения каналов,
 * системный автор `0`) она не знает и отвечает 422. Пока схема не расширена
 * (см. отчёт: `api/src/modules/admin/views.ts`), расширения едут в
 * разрешённом контракте `filters.fields` под служебным ключом: движок
 * фильтров поля с неизвестным кодом игнорирует, поэтому срез от этого не
 * меняется, а представление сохраняется целиком, а не наполовину.
 */
const EXTRA_KEY = '__ex'

const EXTRA_KEYS = [
  'categoriesMode', 'whiteGlove', 'channelDiff', 'onlyOneChannel',
  'onlyMine', 'lensVisit', 'todo', 'myMissing', 'exclude',
] as const

/** Рабочие фильтры → вид, который принимает API. */
export function toStoredFilters(filters: BrandFilters): BrandFilters {
  const stored: Record<string, unknown> = {}
  const extra: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(filters)) {
    if ((EXTRA_KEYS as readonly string[]).includes(key)) extra[key] = value
    else stored[key] = value
  }
  /* `changedBy` в схеме — только положительные id, а импорт это id 0. */
  const authors = filters.changedBy ?? []
  if (authors.includes(0)) {
    extra.systemAuthor = true
    const rest = authors.filter((id) => id > 0)
    if (rest.length > 0) stored.changedBy = rest
    else delete stored.changedBy
  }
  if (Object.keys(extra).length > 0) {
    stored.fields = { ...(filters.fields ?? {}), [EXTRA_KEY]: extra }
  }
  return stored as BrandFilters
}

/** Сохранённые фильтры → рабочий вид. */
export function fromStoredFilters(stored: BrandFilters): BrandFilters {
  const raw = stored.fields?.[EXTRA_KEY]
  if (!raw || typeof raw !== 'object') return stored
  const extra = raw as Record<string, unknown>
  const fields = { ...stored.fields }
  delete fields[EXTRA_KEY]
  const next: Record<string, unknown> = { ...stored }
  if (Object.keys(fields).length > 0) next.fields = fields
  else delete next.fields
  for (const key of EXTRA_KEYS) {
    if (extra[key] !== undefined) next[key] = extra[key]
  }
  if (extra.systemAuthor === true) {
    next.changedBy = [...(stored.changedBy ?? []), SYSTEM_AUTHOR_ID].sort((a, b) => a - b)
  }
  return next as BrandFilters
}

export type ViewKind = 'builtin' | 'mode' | 'personal' | 'shared'

export interface ViewOption {
  /** Код встроенного представления или строковый id сохранённого. */
  id: string
  name: string
  kind: ViewKind
  config: ViewConfig
  /** id в базе — только у сохранённых: их можно переименовать и удалить. */
  savedId?: number
  hint?: string
}

/** Канал `both` в конфиге означает «представление не меняет канал» (§4.6). */
const KEEP_CHANNEL: ViewConfig['channel'] = 'both'

function viewConfig(filters: BrandFilters, sort?: SortRule[], columns?: string[]): ViewConfig {
  return {
    channel: KEEP_CHANNEL,
    filters,
    sort: sort ?? SORT_PRESETS.alphabet,
    columns: columns ?? null,
  }
}

/**
 * Стартовый набор общих представлений (§10.4). В спеке он засеивается
 * миграцией; пока в базе его нет, набор живёт здесь — иначе первый чип
 * «Все бренды» (П4) и ссылки вида `?view=seo` работать не будут.
 */
export const BUILTIN_VIEWS: readonly ViewOption[] = [
  { id: 'all', name: filterTexts.views.all, kind: 'builtin', config: viewConfig({}) },
  {
    id: 'todo', name: filterTexts.views.todo, kind: 'builtin',
    hint: filterTexts.views.todoHint, config: viewConfig({ todo: true }),
  },
  {
    id: 'my-missing', name: filterTexts.views.myMissing, kind: 'builtin',
    hint: filterTexts.views.myMissingHint,
    config: viewConfig({ myMissing: true }, SORT_PRESETS.unfilled),
  },
  { id: 'seo', name: filterTexts.views.seoUnset, kind: 'builtin', config: viewConfig({ seoPriority: [UNSET] }) },
  { id: 'context', name: filterTexts.views.contextUnset, kind: 'builtin', config: viewConfig({ contextPriority: [UNSET] }) },
  { id: 'tier', name: filterTexts.views.tierUnset, kind: 'builtin', config: viewConfig({ tiers: [UNSET] }) },
  { id: 'hidden', name: filterTexts.views.hiddenFromSite, kind: 'builtin', config: viewConfig({ visibility: ['no'] }) },
  {
    id: 'compare', name: filterTexts.views.compare, kind: 'builtin',
    config: viewConfig({ presentIn: 'both' }, SORT_PRESETS.alphabet, ['brand', 'present', 'tier', 'diff']),
  },
  {
    id: 'ap-a-nourl', name: filterTexts.views.apANoUrl, kind: 'builtin',
    config: viewConfig({ tiers: ['A+', 'A'], hasUrl: false }, SORT_PRESETS.priority),
  },
]

/** Персональный режим по зоне ответственности смотрящего (§10.4, §4.9). */
function personalMode(area: string | null): ViewOption | null {
  switch (area) {
    case 'seo':
      return { id: 'my-seo', name: filterTexts.views.mySeo, kind: 'mode', config: viewConfig({ seoPriority: [UNSET] }) }
    case 'marketing':
      return { id: 'my-context', name: filterTexts.views.myContext, kind: 'mode', config: viewConfig({ contextPriority: [UNSET] }) }
    case 'commerce':
      return { id: 'my-tiers', name: filterTexts.views.myTiers, kind: 'mode', config: viewConfig({ tiers: [UNSET] }) }
    case 'site':
      return { id: 'my-site', name: filterTexts.views.mySite, kind: 'mode', config: viewConfig({ todo: true }) }
    default:
      return null
  }
}

/** Стартовое представление по роли (§10.4, таблица «Человек → Старт»). */
const ROLE_START_VIEW: Record<string, string> = {
  owner: 'all',
  admin: 'all',
  commercial: 'tier',
  seo: 'seo',
  marketing: 'context',
  pm: 'todo',
  editor: 'all',
  contributor: 'all',
}

/**
 * Поля зоны ответственности (§4.9). `admin` значит «все», но White Glove в
 * расчёт пропусков не входит: эти три поля пусты у всех 295 брендов, и
 * «Пропущено 3» у каждой строки не диагностика, а шум.
 */
const AREA_FIELDS: Record<string, readonly string[]> = {
  commerce: ['tier', 'visibility', 'categories'],
  seo: ['seoPriority'],
  marketing: ['contextPriority'],
  site: ['url', 'stockDisplay', 'oos'],
  admin: ['tier', 'visibility', 'url', 'stockDisplay', 'oos', 'seoPriority', 'contextPriority'],
}

// ── локальный список сохранённых представлений ──────────────────────────────
/* Стор приложения представления не держит (они приходят в bootstrap один раз),
   а менять `store/app-store.ts` этому модулю нельзя. Поэтому список живёт
   здесь: засеивается из bootstrap, обновляется после создания и удаления. */

const NO_VIEWS: readonly SavedView[] = []
let savedViews: readonly SavedView[] = NO_VIEWS
let savedViewsLoaded = false
const viewListeners = new Set<() => void>()

function publishViews(next: readonly SavedView[]): void {
  savedViews = next
  savedViewsLoaded = true
  for (const listener of viewListeners) listener()
}

function subscribeViews(listener: () => void): () => void {
  viewListeners.add(listener)
  return () => viewListeners.delete(listener)
}

function viewsSnapshot(): readonly SavedView[] {
  return savedViews
}

async function reloadViews(): Promise<void> {
  const res = await api.views()
  publishViews(res.items)
}

/**
 * Созданное представление приходит объектом, а не `{view}`: так зафиксировано
 * в `docs/API-CONTRACT.md` («Создание сущности — 201 + объект созданной
 * сущности»), а типы в `lib/api.ts` обещают обёртку. Разбираем оба вида, пока
 * расхождение не поправлено (см. отчёт).
 */
function unwrapView(res: { view: SavedView }): SavedView | null {
  const body: unknown = res
  if (!body || typeof body !== 'object') return null
  const wrapped = (body as { view?: unknown }).view
  if (wrapped && typeof wrapped === 'object' && 'id' in wrapped) return wrapped as SavedView
  return 'id' in body ? (body as unknown as SavedView) : null
}

// ───────────────────────────── публичный тип состояния ───────────────────────

export interface BrandFiltersState {
  // ── обязательный контракт модуля ──
  filters: BrandFilters
  sort: SortRule[]
  channel: ChannelCode
  setFilters: (next: BrandFilters) => void
  setSort: (next: SortRule[]) => void
  /** Снимок строк: отфильтровано и отсортировано (§5.4). */
  visibleBrands: Brand[]
  /** Растёт при каждом пересчёте снимка — поводе обновить виртуализацию (§5.4). */
  resultRevision: number
  /** Вселенная счётчика «Показано N из M» — бренды активного канала (П7). */
  totalBrands: number
  chips: ChipCounts

  // ── остальное состояние строки управления ──
  setChannel: (next: ChannelCode) => void
  /** Сколько групп фильтра включено: «⚗ Фильтры (N)». */
  activeCount: number
  clearAll: () => void
  /** Строки, которые уйдут из фильтра после «Обновить» (§5.4). */
  staleBrandIds: ReadonlySet<number>
  refresh: () => void
  copyLink: () => void

  sortPreset: SortPresetCode | 'custom'
  setSortPreset: (code: SortPresetCode) => void
  /** Клик по заголовку колонки: asc → desc → снять; `additive` — Shift+клик. */
  toggleColumnSort: (column: string, additive?: boolean) => void

  views: ViewOption[]
  pinnedViews: ViewOption[]
  activeView: ViewOption | null
  /** Параметры переопределяют представление: рядом появляется «Сохранить вид» (§10.5). */
  viewOverridden: boolean
  applyView: (view: ViewOption | null) => void
  canShareViews: boolean
  saveView: (name: string, isShared: boolean) => Promise<void>
  renameView: (view: ViewOption, name: string, isShared: boolean) => Promise<void>
  removeView: (view: ViewOption) => Promise<void>

  /** Контекст ядра: нужен таблице для «Пропущено» и подсветки поиска. */
  context: FilterContext
  /** Набор колонок из представления, если оно его задаёт (§10.5, `cols`). */
  columns: string[] | null
}

// ───────────────────────────── хук ───────────────────────────────────────────

export function useBrandFilters(): BrandFiltersState {
  const [params, setParams] = useSearchParams()
  const search = params.toString()

  const bootstrap = useApp((s) => s.bootstrap)
  const brands = useApp((s) => s.brands)
  const overlay = useApp((s) => s.overlay)
  const reloadBrands = useApp((s) => s.reloadBrands)
  const toast = useApp((s) => s.toast)

  const fields = useMemo(() => bootstrap?.fields ?? [], [bootstrap])
  const user = bootstrap?.user ?? null
  const defaultChannel = bootstrap?.settings.defaultChannel ?? 'retail'
  const defaultSort = bootstrap?.settings.defaultSort ?? 'alphabet'

  const savedList = useSyncExternalStore(subscribeViews, viewsSnapshot, viewsSnapshot)

  /* Представления: засеиваем из bootstrap, затем освежаем с сервера. */
  useEffect(() => {
    if (!bootstrap || savedViewsLoaded) return
    publishViews(bootstrap.views)
    void reloadViews().catch(() => {/* список останется из bootstrap */})
  }, [bootstrap])

  // ── чтение состояния из URL ──
  const channel: ChannelCode = useMemo(() => {
    const raw = new URLSearchParams(search).get('ch')
    return raw === 'retail' || raw === 'b2b' ? raw : defaultChannel
  }, [search, defaultChannel])

  const filters = useMemo(
    () => readFilters(new URLSearchParams(search), fields),
    [search, fields],
  )

  const fallbackSort = defaultSort === 'tier' ? SORT_PRESETS.priority : SORT_PRESETS.alphabet
  const sort = useMemo(
    () => readSort(new URLSearchParams(search), fallbackSort),
    [search, fallbackSort],
  )

  const columns = useMemo(() => {
    const raw = new URLSearchParams(search).get('cols')
    if (!raw) return null
    const list = raw.split(',').map((value) => value.trim()).filter(Boolean)
    return list.length > 0 ? list : null
  }, [search])

  // ── представления ──
  const views = useMemo<ViewOption[]>(() => {
    const mode = personalMode(user?.area ?? null)
    const saved = savedList.map<ViewOption>((view) => ({
      id: String(view.id),
      name: view.name,
      kind: view.isShared ? 'shared' : 'personal',
      config: view.config,
      savedId: view.id,
    }))
    const builtins = BUILTIN_VIEWS.filter(
      (view) => view.id !== 'my-missing' || (user?.area ?? null) !== null,
    )
    /* «Все бренды» — всегда первый (П4), дальше персональный режим роли. */
    const [all, ...rest] = builtins
    const head = all ? [all] : []
    return [...head, ...(mode ? [mode] : []), ...rest, ...saved]
  }, [savedList, user])

  const viewParam = useMemo(() => new URLSearchParams(search).get('view'), [search])
  const activeView = useMemo(
    () => views.find((view) => view.id === viewParam) ?? null,
    [views, viewParam],
  )

  const viewOverridden = useMemo(() => {
    if (!activeView) return false
    const expected = new URLSearchParams()
    writeFilters(expected, fromStoredFilters(activeView.config.filters as BrandFilters))
    const current = new URLSearchParams()
    writeFilters(current, filters)
    expected.sort()
    current.sort()
    const sortMatches = sameSort(activeView.config.sort ?? fallbackSort, sort)
    return expected.toString() !== current.toString() || !sortMatches
  }, [activeView, filters, sort, fallbackSort])

  const pinnedViews = useMemo(() => {
    const order = ['all', personalMode(user?.area ?? null)?.id, 'todo', 'my-missing', 'compare']
    const pinned = order
      .filter((id): id is string => typeof id === 'string')
      .map((id) => views.find((view) => view.id === id))
      .filter((view): view is ViewOption => view !== undefined)
    /* Активное представление всегда видно чипом, даже если не закреплено. */
    if (activeView && !pinned.some((view) => view.id === activeView.id)) {
      return [...pinned.slice(0, 4), activeView]
    }
    return pinned.slice(0, 5)
  }, [views, user, activeView])

  // ── контекст ядра ──
  const areaFields = useMemo(() => {
    const area = user?.area ?? null
    return area ? AREA_FIELDS[area] ?? [] : []
  }, [user])

  const enumOrder = useMemo(() => {
    const map = new Map<string, Map<string, number>>()
    for (const [set, values] of Object.entries(bootstrap?.enums ?? {})) {
      const inner = new Map<string, number>()
      for (const value of values) inner.set(value.value, value.sortOrder)
      map.set(set, inner)
    }
    return map
  }, [bootstrap])

  /** Черновик не пересчитывает снимок (§5.4): читаем его через ref. */
  const draftRef = useRef<{ draft: Set<number>; created: Set<number> }>({
    draft: new Set(), created: new Set(),
  })
  const draftIds = useMemo(() => {
    const draft = new Set<number>()
    for (const brand of brands) if (brandHasDraft(overlay, brand)) draft.add(brand.id)
    return draft
  }, [brands, overlay])
  draftRef.current = { draft: draftIds, created: overlay.draftBrands }

  const context = useMemo<FilterContext>(() => ({
    fields,
    enumOrder,
    areaFields,
    draftBrandIds: draftIds,
    newBrandIds: overlay.draftBrands,
  }), [fields, enumOrder, areaFields, draftIds, overlay])

  /* Контекст для снимка: без черновика в зависимостях — иначе каждая правка
     пересчитывала бы набор строк вопреки §5.4. */
  const snapshotContext = useMemo<FilterContext>(() => ({
    fields,
    enumOrder,
    areaFields,
  }), [fields, enumOrder, areaFields])

  // ── снимок строк ──
  const revisionRef = useRef(0)
  const visibleBrands = useMemo(() => {
    revisionRef.current += 1
    const own = new URLSearchParams(search)
    const snapshotFilters = readFilters(own, fields)
    const snapshotSort = readSort(own, fallbackSort)
    const ctx: FilterContext = {
      ...snapshotContext,
      draftBrandIds: draftRef.current.draft,
      newBrandIds: draftRef.current.created,
    }
    const found = applyFilters(brands, snapshotFilters, channel, ctx)
    return sortBrands(found, snapshotSort, channel, ctx)
    // Пересчёт ровно по поводам из §5.4: фильтр, сортировка, канал, новый срез.
  }, [brands, search, channel, fields, fallbackSort, snapshotContext])

  // ── счётчики ──
  /* Вселенную задаёт только статус (активные / архив / все): от остальных
     фильтров счётчики чипов не зависят — иначе у чипа «A+ 19» рядом стояла бы
     подпись «из 230», которой число уже не соответствует (П7). */
  const statusFilter = filters.status ?? 'active'
  const universe = useMemo(
    () => brands.filter((brand) => matchesStatus(brand, { status: statusFilter })),
    [brands, statusFilter],
  )

  const frozenChips = useMemo(
    () => countChips(universe, channel, snapshotContext),
    [universe, channel, snapshotContext],
  )

  /* Единственный счётчик, который обязан быть живым: он про мой черновик. */
  const chips = useMemo<ChipCounts>(
    () => ({ ...frozenChips, mine: draftIds.size }),
    [frozenChips, draftIds],
  )

  // ── строки, разошедшиеся со снимком (§5.4) ──
  const staleBrandIds = useMemo(() => {
    const stale = new Set<number>()
    if (overlay.count === 0) return stale
    const ctx: FilterContext = { ...context, overlay }
    for (const brand of visibleBrands) {
      if (overlay.draftBrands.has(brand.id)) continue
      if (!brandHasDraft(overlay, brand)) continue
      if (!matchesFilters(brand, filters, channel, ctx)) stale.add(brand.id)
    }
    return stale
  }, [visibleBrands, filters, channel, context, overlay])

  // ── запись в URL ──
  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setParams((prev) => {
        const next = new URLSearchParams(prev)
        mutate(next)
        next.set('ch', next.get('ch') ?? defaultChannel)
        return next
      }, { replace: true })
    },
    [setParams, defaultChannel],
  )

  const setFilters = useCallback((next: BrandFilters) => {
    update((params) => {
      writeFilters(params, next)
    })
  }, [update])

  const setSort = useCallback((next: SortRule[]) => {
    update((params) => {
      if (next.length === 0 || sameSort(next, fallbackSort)) params.delete('sort')
      else params.set('sort', next.map(writeSort).join(','))
    })
  }, [update, fallbackSort])

  const setChannel = useCallback((next: ChannelCode) => {
    update((params) => {
      params.set('ch', next)
    })
  }, [update])

  const clearAll = useCallback(() => {
    update((params) => {
      writeFilters(params, {})
      params.delete('view')
    })
  }, [update])

  const setSortPreset = useCallback((code: SortPresetCode) => {
    setSort(SORT_PRESETS[code])
  }, [setSort])

  const sortPreset = useMemo<SortPresetCode | 'custom'>(() => {
    for (const code of Object.keys(SORT_PRESETS) as SortPresetCode[]) {
      if (sameSort(SORT_PRESETS[code], sort)) return code
    }
    return 'custom'
  }, [sort])

  const toggleColumnSort = useCallback((column: string, additive = false) => {
    const current = sort.find((rule) => rule.column === column)
    const rest = sort.filter((rule) => rule.column !== column)
    /* asc → desc → снять (§10.3). Shift+клик добавляет уровень к текущим. */
    const nextRule: SortRule | null = current === undefined
      ? { column, direction: 'asc' }
      : current.direction === 'asc' ? { column, direction: 'desc' } : null
    if (additive) {
      setSort(nextRule ? [...rest, nextRule] : rest)
      return
    }
    setSort(nextRule ? [nextRule] : [])
  }, [sort, setSort])

  const applyView = useCallback((view: ViewOption | null) => {
    update((params) => {
      if (!view) {
        params.delete('view')
        return
      }
      writeFilters(params, fromStoredFilters(view.config.filters as BrandFilters))
      const viewSort = view.config.sort ?? []
      if (viewSort.length === 0 || sameSort(viewSort, fallbackSort)) params.delete('sort')
      else params.set('sort', viewSort.map(writeSort).join(','))
      if (view.config.channel === 'retail' || view.config.channel === 'b2b') {
        params.set('ch', view.config.channel)
      }
      if (view.config.columns && view.config.columns.length > 0) {
        params.set('cols', view.config.columns.join(','))
      } else {
        params.delete('cols')
      }
      params.set('view', view.id)
    })
  }, [update, fallbackSort])

  /* Стартовое представление роли — один раз, и только если ссылка пустая:
     чужая ссылка со своим состоянием важнее настройки по умолчанию (§10.5). */
  const startedRef = useRef(false)
  useEffect(() => {
    if (startedRef.current || !user) return
    const current = new URLSearchParams(window.location.search)
    const hasOwn = OWNED.some((param) => current.has(param))
      || [...current.keys()].some((param) => param.startsWith(FIELD_PREFIX))
    startedRef.current = true
    if (hasOwn) return
    const code = ROLE_START_VIEW[user.roleCode] ?? 'all'
    const view = views.find((item) => item.id === code)
    if (view && view.id !== 'all') applyView(view)
  }, [user, views, applyView])

  /* `ch` обязателен (§10.5): без него ссылка неполная. */
  useEffect(() => {
    if (params.get('ch')) return
    update(() => {/* update сам допишет ch */})
  }, [params, update])

  const refresh = useCallback(() => {
    void reloadBrands()
  }, [reloadBrands])

  const copyLink = useCallback(() => {
    const url = window.location.href
    const done = () => toast('success', filterTexts.counters.copied)
    const failed = () => toast('error', filterTexts.counters.copyFailed)
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done, failed)
      return
    }
    failed()
  }, [toast])

  // ── сохранение представлений ──
  const canShareViews = user?.permissions.includes('views.share') ?? false

  const currentConfig = useCallback((): ViewConfig => ({
    channel,
    filters: toStoredFilters(filters),
    sort,
    columns,
  }), [channel, filters, sort, columns])

  const saveView = useCallback(async (name: string, isShared: boolean) => {
    const res = await api.createView({ name, isShared: isShared && canShareViews, config: currentConfig() })
    const view = unwrapView(res)
    if (!view) {
      await reloadViews()
      return
    }
    publishViews([...savedViews, view])
    update((params) => {
      params.set('view', String(view.id))
    })
    toast('success', filterTexts.views.created(view.name))
  }, [canShareViews, currentConfig, update, toast])

  const renameView = useCallback(async (view: ViewOption, name: string, isShared: boolean) => {
    if (view.savedId === undefined) return
    const res = await api.updateView(view.savedId, { name, isShared: isShared && canShareViews })
    const saved = unwrapView(res)
    if (!saved) {
      await reloadViews()
      return
    }
    publishViews(savedViews.map((item) => (item.id === saved.id ? saved : item)))
    toast('success', filterTexts.views.renamed)
  }, [canShareViews, toast])

  const removeView = useCallback(async (view: ViewOption) => {
    if (view.savedId === undefined) return
    await api.deleteView(view.savedId)
    publishViews(savedViews.filter((item) => item.id !== view.savedId))
    if (viewParam === view.id) applyView(null)
    toast('success', filterTexts.views.removed(view.name))
  }, [viewParam, applyView, toast])

  return {
    filters,
    sort,
    channel,
    setFilters,
    setSort,
    visibleBrands,
    resultRevision: revisionRef.current,
    totalBrands: chips.channelTotal,
    chips,

    setChannel,
    activeCount: countActiveGroups(filters),
    clearAll,
    staleBrandIds,
    refresh,
    copyLink,

    sortPreset,
    setSortPreset,
    toggleColumnSort,

    views,
    pinnedViews,
    activeView,
    viewOverridden,
    applyView,
    canShareViews,
    saveView,
    renameView,
    removeView,

    context,
    columns,
  }
}
